"use client";

import { useState, useSyncExternalStore } from "react";
import PricingContent from "./PricingContent";
import { getCoupon, subscribeToCouponStore } from "@/lib/coupon";

const SGN20_COUPON = "SGN20";
const SGN35_COUPON = "SGN35";

function couponToForcedTab(coupon: string | null): "monthly" | "yearly" | null {
  if (!coupon) return null;
  const upper = coupon.toUpperCase();
  if (upper === SGN20_COUPON) return "monthly";
  if (upper === SGN35_COUPON) return "yearly";
  return null;
}

// useSyncExternalStore snapshot getters. The server snapshot returns null so
// the initial client render matches the server-rendered HTML; React then
// transitions to the live client value after hydration.
const getCouponClientSnapshot = (): string | null => getCoupon();
const getCouponServerSnapshot = (): string | null => null;

export function PricingSection() {
  // SSR-safe read of the coupon from localStorage / URL. React subscribes via
  // subscribeToCouponStore so PricingContent's mount-time captureCouponFromUrl()
  // — and any cross-tab storage events — trigger a re-render here too.
  const coupon = useSyncExternalStore(
    subscribeToCouponStore,
    getCouponClientSnapshot,
    getCouponServerSnapshot,
  );
  const forcedTab = couponToForcedTab(coupon);

  // User's billing-period preference. If `forcedTab` is set (SGN20/SGN35),
  // it overrides the user toggle below.
  const [userPlanType, setUserPlanType] = useState<"monthly" | "yearly">("yearly");
  const planType: "monthly" | "yearly" = forcedTab ?? userPlanType;
  const setPlanType = setUserPlanType;

  return (
    <section
      id="pricing"
      className="relative flex flex-col items-center py-20 px-4 md:px-[150px] overflow-hidden"
      style={{ isolation: "isolate", gap: "48px" }}
    >
      {/* Gradient Blob Background */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: "529px",
          height: "557px",
          left: "calc(50% - 360.71px)",
          top: "304px",
          background:
            "linear-gradient(319.89deg, rgba(65, 88, 208, 0.4) 4.65%, rgba(200, 80, 192, 0.4) 61.9%, rgba(255, 204, 112, 0.4) 121.69%)",
          filter: "blur(100px)",
          transform: "rotate(-35.61deg)",
          zIndex: 0,
        }}
      />

      {/* Title + Toggle */}
      <div
        className="flex flex-col items-center w-full"
        style={{ gap: "40px", zIndex: 1, maxWidth: "1140px" }}
      >
        <div
          className="flex w-full flex-col items-center justify-center text-center"
          style={{ gap: "24px", maxWidth: "1140px", minHeight: "184px" }}
        >
          <h2
            className="flex w-full items-center justify-center text-center"
            style={{
              fontFamily: "'Hanken Grotesk', sans-serif",
              fontStyle: "normal",
              fontWeight: 700,
              fontSize: "clamp(36px, 4.5vw, 52px)",
              lineHeight: "clamp(44px, 5.9vw, 68px)",
              color: "#1B1C30",
            }}
          >
            Affordable Plans to Grow Your Amazon <br /> Sales Faster
          </h2>
          <p
            className="w-full text-center"
            style={{
              maxWidth: "620px",
              fontFamily: "'Figtree', sans-serif",
              fontStyle: "normal",
              fontWeight: 400,
              fontSize: "18px",
              lineHeight: "24px",
              color: "#262626",
            }}
          >
            Each action enhances your product listing with meaningful improvements.
          </p>
        </div>

        {/* Billing Toggle — both options always render. The discount (if any)
            is resolved per-plan-type server-side and decorated on each plan
            card independently; the toggle never locks. */}
        <div className="flex items-center gap-2 bg-white rounded-[30px] px-4 py-2">
          {forcedTab !== "yearly" && (
            <button
              onClick={() => setPlanType("monthly")}
              className="flex items-center justify-center rounded-full transition-all duration-200 text-sm font-semibold"
              style={{
                width: "110px",
                height: "44px",
                background: planType === "monthly" ? "#1464FF" : "transparent",
                color: planType === "monthly" ? "#FFFFFF" : "#4D4D5C",
              }}
            >
              Monthly
            </button>
          )}
          {forcedTab !== "monthly" && (
            <button
              onClick={() => setPlanType("yearly")}
              className="flex items-center justify-center rounded-full transition-all duration-200 text-sm font-semibold"
              style={{
                width: "110px",
                height: "44px",
                background: planType === "yearly" ? "#1464FF" : "transparent",
                color: planType === "yearly" ? "#FFFFFF" : "#4D4D5C",
              }}
            >
              Yearly
            </button>
          )}
          {forcedTab === null && (
            <div
              className="flex items-center gap-1 rounded-full px-3 py-2 ml-auto"
              style={{ background: "rgba(40, 199, 111, 0.16)" }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.5 13.5L3.5 9.5M3.5 9.5L7.5 5.5M3.5 9.5H9.5C11.7091 9.5 13.5 11.2909 13.5 13.5V13.5"
                  stroke="#28C76F"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-sm font-medium leading-tight text-[#28C76F] whitespace-nowrap">
                Save 2 months
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Plans Grid — client-only, never SSR'd */}
      <div style={{ zIndex: 2, maxWidth: "1260px", width: "100%" }}>
        <PricingContent planType={planType} />
      </div>
    </section>
  );
}
